import React, { useEffect, useRef } from 'react';
import { FocusOn } from 'react-focus-on';
import classnames from 'classnames';
import { useOutsideClick } from '../../hooks/use-outside-click';
import { Text } from '../text';
import { Heading, HeadingProps } from '../heading';

import styles from './dialog.module.scss';

export type DialogProps = {
  isOpen: boolean;
  outsideClick: () => void;
  ariaDescription: string;
  dialogLabelledBy?: string;
  text: string;
  headline: Omit<HeadingProps, 'variant'>;
  buttons: React.ReactNode;
} & Pick<React.HTMLAttributes<HTMLDivElement>, 'className'>;

const baseClass = 'dialog';
const noScrollClass = 'no-scroll';

export const Dialog: React.FC<DialogProps> = ({
  isOpen,
  headline,
  text,
  outsideClick,
  ariaDescription,
  dialogLabelledBy,
  buttons,
  className
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const classes = classnames(styles.dialog, { [styles[`${baseClass}--open`]]: isOpen }, className);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add(noScrollClass);

      if (ref.current) {
        ref.current.addEventListener(
          'scroll',
          () => {
            if (ref.current) {
              ref.current.scrollTop = 0;
            }
          },

          // If true, the listener would be automatically removed when invoked.
          { once: true }
        );
      }
    } else document.body.classList.remove(noScrollClass);
  }, [isOpen]);

  useOutsideClick(ref, outsideClick);

  return (
    <>
      <FocusOn enabled={isOpen} onEscapeKey={outsideClick}>
        <div
          aria-hidden={!isOpen}
          className={classes}
          aria-modal={isOpen}
          aria-describedby={`${baseClass}-description`}
          data-testid="dialog-component"
          aria-labelledby={dialogLabelledBy || ''}
        >
          <div className={styles[`${baseClass}__inner`]} ref={ref}>
            <div>
              <Heading {...headline} variant="headline-functional-400" className={styles[`${baseClass}__headline`]}>
                {headline.children}
              </Heading>
              <Text className={styles[`${baseClass}__text`]}>{text}</Text>
            </div>
            <div className={styles[`${baseClass}__button-container`]}>{buttons}</div>
          </div>
          <div className={styles[`${baseClass}__hidden-description`]} id={`${baseClass}-description`}>
            {ariaDescription}
          </div>
        </div>
      </FocusOn>
      {isOpen && <div className={styles[`${baseClass}-layer`]} />}
    </>
  );
};
